import { Box, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  useGetEstablishments,
  useGetEstablishmentsSuggestions,
} from '@establishment/queries/useGetEstablishment';

import EstablishmentRow from '../EstablishmentRow';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import IOption from '@models/option';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import { JSelect } from '@atoms/JSelect';
import { JTable } from '@atoms/JTable';
import { JTablePagination } from '@atoms/JTablePagination';
import { KEY_ORDER } from '@models/key-order';
import { SearchTextField } from '@atoms/SearchTextField';
import TableColumn from '@models/table-column';
import { useCities } from '@/queries/useCities';
import { useDebouncedCallback } from 'use-debounce';
import useEstablishments from '@establishment/hooks/useEstablishments';
import { useGetEnterprisesSuggestions } from '@enterprise/queries/useGetEnterprises';
import { useTranslation } from 'react-i18next';

const TABLE_COLS: TableColumn[] = [
  { name: 'NAME', orderable: true, sortingField: 'establishment_name' },
  { name: 'SIRET', orderable: true, sortingField: 'establishment_siret' },
  { name: 'POSTAL_CODE', orderable: true, sortingField: 'city_zip_code' },
  { name: 'CITY', orderable: true, sortingField: 'city_name' },
  { name: 'STATUS', orderable: true, sortingField: 'establishment.deleted_at' },
  { name: 'ENTERPRISE', orderable: true, sortingField: 'enterprise_name' },
  {
    name: 'USERS_COUNT',
    orderable: true,
    sortingField: 'userscount',
  },
];

const PER_PAGE = 20;
const DEFAULT_ORDER = {
  order: KEY_ORDER.DESC,
  orderBy: 'enterprise.deleted_at',
};

const EstablishmentsList: React.FC = () => {
  const { t } = useTranslation();
  const { setEstablishmentsCount } = useEstablishments();
  const [page, setPage] = useState<number>();
  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(DEFAULT_ORDER);

  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>([]);
  const [cityInputValue, setCityInputValue] = useState<string>('');
  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');
  const [establishmentInputValue, setEstablishmenInputValue] =
    useState<string>('');
  const { data: cities } = useCities(10, 1, cityInputValue);
  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );
  const { data: establishments } = useGetEstablishmentsSuggestions(
    10,
    1,
    establishmentInputValue,
  );

  const { data } = useGetEstablishments(
    {
      page,
      limit: PER_PAGE,
      order: order.order,
      orderBy: order.orderBy,
    },
    {
      searchCriteria: searchFilter,
    },
  );

  const rows = data?.data.data;
  const meta = data?.data.meta;
  const pagesCount = data?.data.meta.itemCount;
  const isNotFound = rows?.length === 0;
  const citiesOptions = cities
    ? cities.data.data.map(({ id, name, zipCode }) => {
        return { value: id, label: `${name} / ${zipCode}` };
      })
    : [];
  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const establishmentsOptions = establishments
    ? establishments.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };

  const appendSearchFilter = (
    field: FilterCriteriaFields,
    value: string,
  ): void => {
    const existed = searchFilter?.some((item) => item.field === field);

    if (!existed) setSearchFilter([...searchFilter, { field, value }]);
    if (value === '')
      setSearchFilter((prevValue) =>
        prevValue.filter((item) => item.field !== field),
      );
    else
      setSearchFilter((prevValue) =>
        prevValue.map((item) =>
          item.field === field ? { field, value } : item,
        ),
      );
  };

  const debouncedAppendSearchFilter = useDebouncedCallback(
    appendSearchFilter,
    1000,
  );

  const debouncedCity = useDebouncedCallback((value) => {
    setCityInputValue(value);
  }, 1000);
  const debouncedEnterprise = useDebouncedCallback((value) => {
    setEnterpriseInputValue(value);
  }, 1000);
  const debouncedEstablishment = useDebouncedCallback((value) => {
    setEstablishmenInputValue(value);
  }, 1000);

  useEffect(() => {
    setEstablishmentsCount(meta?.itemCount);
  }, [meta?.itemCount]);

  return (
    <>
      <Stack marginY={3} direction="row" gap={2}>
        <JSelect
          options={[
            { label: 'Tous', value: '' },
            { label: 'Active', value: 'true' },
            { label: 'Inactive', value: 'false' },
          ]}
          size="small"
          label="Statut"
          onChange={(event) =>
            appendSearchFilter(
              FilterCriteriaFields.ESTABLISHMENT_STATUS,
              event.target.value as string,
            )
          }
        />

        <JAutocomplete
          label={t('ESTABLISHMENT')}
          options={establishmentsOptions}
          onChange={(ev, newValue) =>
            appendSearchFilter(
              FilterCriteriaFields.ESTABLISHMENT_ID,
              (newValue as IOption)?.value || '',
            )
          }
          onTextChange={(val) => debouncedEstablishment(val)}
        />

        <JAutocomplete
          label={t('ENTERPRISE')}
          options={enterprisesOptions}
          onChange={(ev, newValue) =>
            appendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_ID,
              (newValue as IOption)?.value || '',
            )
          }
          onTextChange={(val) => debouncedEnterprise(val)}
        />

        <SearchTextField
          label="Siret"
          onChange={(value) =>
            debouncedAppendSearchFilter(
              FilterCriteriaFields.ESTABLISHMENT_SIRET,
              value,
            )
          }
        />

        <SearchTextField
          label="Siren"
          onChange={(value) =>
            debouncedAppendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_SIREN,
              value,
            )
          }
        />

        <JAutocomplete
          label={t('CITY_ZIP_CODE')}
          options={citiesOptions}
          onChange={(ev, newValue) =>
            appendSearchFilter(
              FilterCriteriaFields.ESTABLISHMENT_CITY_ID,
              (newValue as IOption)?.value || '',
            )
          }
          onTextChange={(val) => debouncedCity(val)}
        />
      </Stack>
      <JTable
        isNotFound={isNotFound}
        columns={TABLE_COLS}
        notFoundLabel={t('NO_ESTABLISHMENT_FOUND') ?? ''}
        total={pagesCount ?? 0}
        perpage={PER_PAGE}
        nbpages={pagesCount ?? 0}
        onChangeOrder={(value) => handleColumnClick(value)}
      >
        {rows?.map((row) => (
          <EstablishmentRow row={row} key={row.establishment_id} />
        ))}
      </JTable>

      {meta && (
        <Box mt={2}>
          <JTablePagination
            onPageChange={(value) => {
              setPage(value);
            }}
            meta={meta}
          />
        </Box>
      )}
    </>
  );
};

export default EstablishmentsList;
